// pages/Register.js
// Registration Page with Firebase Authentication
// ============================================
// Supports Google Sign-Up and Email/Password
// Captures referral codes, ZIP code, displays welcome bonus
// Routes to QuickIntro after success
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Gift, AlertCircle, CheckCircle, Coins, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SocialAuthButtons from '../components/auth/SocialAuthButtons';
import { TOKEN_CONFIG, formatTokenValue } from '../utils/tokenEconomy';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { 
    registerWithEmail, 
    isLoggedIn, 
    isNewUser,
    loading: authLoading, 
    error: authError, 
    clearError,
    saveReferralCode
  } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  
  // Success state
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [tokensAwarded, setTokensAwarded] = useState(0);
  const [zipCode, setZipCode] = useState('');
  const [zipError, setZipError] = useState('');
  const [savingZip, setSavingZip] = useState(false);
  
  // Get redirect destination and referral code from URL
  const from = location.state?.from?.pathname || '/home';
  const urlReferralCode = searchParams.get('ref') || searchParams.get('referral') || '';
  
  // Set referral code from URL on mount
  useEffect(() => {
    if (urlReferralCode) {
      setFormData(prev => ({ ...prev, referralCode: urlReferralCode }));
      saveReferralCode?.(urlReferralCode);
    }
  }, [urlReferralCode, saveReferralCode]);
  
  // Only redirect if already logged in BEFORE they started registration
  // Don't redirect during/after sign-up process
  useEffect(() => {
    // If showing success or in the middle of registration, don't redirect
    if (registrationSuccess || loading || authLoading) {
      return;
    }
    
    // Only redirect if they were already logged in when they arrived
    // AND they haven't just completed registration
    if (isLoggedIn && !isNewUser && !registrationSuccess) {
      // Check if this is a fresh page load vs post-registration
      const justRegistered = sessionStorage.getItem('uniledge_just_registered');
      if (!justRegistered) {
        navigate(from, { replace: true });
      }
    }
  }, []);
  
  // Clear errors on mount
  useEffect(() => {
    clearError?.();
    return () => clearError?.();
  }, [clearError]);
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error on input change
  };
  
  // Validate form
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email');
      return false;
    }
    if (!formData.password) {
      setError('Please enter a password');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!agreeToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return false;
    }
    return true;
  };
  
  // Validate ZIP code
  const validateZipCode = (zip) => {
    // US ZIP code format: 5 digits or 5+4 format
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip);
  };
  
  // Handle email/password registration
  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const result = await registerWithEmail(
        formData.email,
        formData.password,
        formData.name,
        formData.referralCode || null
      );
      
      if (result.success) {
        // Set flag to prevent redirect useEffect from firing
        sessionStorage.setItem('uniledge_just_registered', 'true');
        setTokensAwarded(result.tokensAwarded || TOKEN_CONFIG.WELCOME_BONUS);
        setRegistrationSuccess(true);
      } else {
        setError(result.error || 'Failed to create account');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle social auth success
  const handleSocialSuccess = (result) => {
    // Set flag to prevent redirect useEffect from firing
    sessionStorage.setItem('uniledge_just_registered', 'true');
    
    if (result.isNew) {
      setTokensAwarded(result.tokensAwarded || TOKEN_CONFIG.WELCOME_BONUS);
      setRegistrationSuccess(true);
    } else {
      // Existing user signed in - redirect
      sessionStorage.removeItem('uniledge_just_registered');
      navigate(from, { replace: true });
    }
  };
  
  // Handle social auth error
  const handleSocialError = (errorMessage) => {
    setError(errorMessage);
  };
  
  // Save ZIP code to backend
  const saveZipCode = async (zip) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
      const response = await fetch(`${apiUrl}/auth/update-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ zipCode: zip })
      });
      
      if (!response.ok) {
        console.warn('Failed to save ZIP code:', response.status);
        // Don't block navigation if ZIP save fails
        return false;
      }
      
      return true;
    } catch (error) {
      console.warn('Error saving ZIP code:', error);
      // Don't block navigation if ZIP save fails
      return false;
    }
  };
  
  // Handle continue after success
  const handleContinue = async () => {
    // Validate ZIP if entered
    if (zipCode && !validateZipCode(zipCode)) {
      setZipError('Please enter a valid 5-digit ZIP code');
      return;
    }
    
    setSavingZip(true);
    
    // Save ZIP code if provided (non-blocking)
    if (zipCode) {
      await saveZipCode(zipCode);
    }
    
    // Mark intro as not completed so QuickIntro shows
    localStorage.removeItem('quick_intro_completed');
    
    // Clear registration flag
    sessionStorage.removeItem('uniledge_just_registered');
    
    // Navigate to QuickIntro
    navigate('/quick-intro', { replace: true });
  };
  
  // Handle skip (no ZIP)
  const handleSkipZip = () => {
    localStorage.removeItem('quick_intro_completed');
    sessionStorage.removeItem('uniledge_just_registered');
    navigate('/quick-intro', { replace: true });
  };
  
  // Calculate total possible bonus
  const hasReferral = !!formData.referralCode;
  const totalBonus = TOKEN_CONFIG.WELCOME_BONUS + (hasReferral ? TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER : 0);
  
  // Success Screen
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-lg sm:rounded-xl sm:px-10 text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to UniLedge! 🎉
            </h2>
            
            <p className="text-gray-600 mb-6">
              Your account has been created successfully.
            </p>
            
            {/* Token Award Card */}
            <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl p-6 text-white mb-6">
              <div className="flex items-center justify-center mb-2">
                <Coins className="w-8 h-8 mr-2" />
                <span className="text-3xl font-bold">{tokensAwarded}</span>
              </div>
              <p className="text-lg font-medium">Welcome Tokens!</p>
              <p className="text-sm opacity-90">
                That's {formatTokenValue(tokensAwarded)} in platform credit
              </p>
            </div>
            
            {/* ZIP Code Input */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-blue-600" />
                  Your ZIP Code
                </div>
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Help us find deals and groups near you
              </p>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => {
                  // Only allow numbers and dash
                  const value = e.target.value.replace(/[^\d-]/g, '');
                  setZipCode(value);
                  setZipError('');
                }}
                maxLength={10}
                placeholder="e.g., 95823"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest ${
                  zipError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {zipError && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {zipError}
                </p>
              )}
            </div>
            
            {/* What's Next */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-gray-800 mb-2">What's Next?</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-xs font-bold text-blue-600">1</span>
                  Quick intro to UniLedge (30 seconds)
                </li>
                <li className="flex items-center">
                  <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-xs font-bold text-blue-600">2</span>
                  Browse deals and save 25-40%
                </li>
                <li className="flex items-center">
                  <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-xs font-bold text-blue-600">3</span>
                  Invite friends for more tokens
                </li>
              </ul>
            </div>
            
            <button
              onClick={handleContinue}
              disabled={savingZip}
              className={`w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors ${
                savingZip ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {savingZip ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                "Let's Get Started →"
              )}
            </button>
            
            {/* Skip option */}
            <button
              onClick={handleSkipZip}
              className="mt-3 text-sm text-gray-500 hover:text-gray-700"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Registration Form
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <Link to="/" className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">UL</span>
          </Link>
        </div>
        
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
            Sign in
          </Link>
        </p>
      </div>
      
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Welcome Bonus Banner */}
          <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                <Gift className="w-5 h-5 text-yellow-900" />
              </div>
              <div>
                <p className="font-semibold text-yellow-900">
                  Get {totalBonus} welcome tokens!
                </p>
                <p className="text-sm text-yellow-700">
                  {formatTokenValue(totalBonus)} in platform credit
                  {hasReferral && ' (includes referral bonus!)'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Error Display */}
          {(error || authError) && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error || authError}</span>
            </div>
          )}
          
          {/* Social Auth Buttons */}
          <SocialAuthButtons
            mode="signup"
            providers={['google']}
            onSuccess={handleSocialSuccess}
            onError={handleSocialError}
            disabled={loading || authLoading}
          />
          
          {/* Email/Password Form */}
          <form onSubmit={handleEmailRegister} className="space-y-5">
            {/* Name Input */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full name
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Doe"
                />
              </div>
            </div>
            
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            
            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">Must be at least 6 characters</p>
            </div>
            
            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>
            
            {/* Referral Code Input */}
            <div>
              <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700">
                Referral code <span className="text-gray-400">(optional)</span>
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Gift className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="referralCode"
                  name="referralCode"
                  type="text"
                  value={formData.referralCode}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter code for bonus tokens"
                />
              </div>
              {hasReferral && (
                <p className="mt-1 text-xs text-green-600 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Referral code applied! You'll both get {TOKEN_CONFIG.REFERRAL_BONUS_NEW_USER} bonus tokens.
                </p>
              )}
            </div>
            
            {/* Terms Checkbox */}
            <div className="flex items-start">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-600">
                I agree to the{' '}
                <Link to="/terms" className="text-blue-600 hover:text-blue-500">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-500">
                  Privacy Policy
                </Link>
              </label>
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || authLoading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                loading || authLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading || authLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating account...
                </span>
              ) : (
                'Create account'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;